import { Link } from "react-router-dom";
import { AlertTriangle, FlaskConical, MessageSquareWarning, PackageCheck, ShieldAlert, Truck } from "lucide-react";
import { formatINR } from "@/lib/utils";
import { SectionCard } from "@/components/reports/ReportWidgets";
import { DonutPieChart } from "@/components/charts/DonutPieChart";
import { RadialTargetGauge } from "@/components/charts/RadialTargetGauge";

interface SampleRow {
  id: string;
  sample_number: string;
  product_name: string;
  account_name: string;
  deal_id: string;
  sample_status?: string;
  needed_by?: string | null;
  overdue?: boolean;
  courier?: string | null;
  awb_number?: string | null;
  days_out?: number;
}

export interface MerchandiserSummary {
  samples_to_send: SampleRow[];
  samples_with_buyers: SampleRow[];
  overdue_samples: number;
  orders_open: number;
  pending_lines: number;
  overdue_lines: number;
  open_complaints: number;
  open_claims: number;
  oldest_complaint: string | null;
}

const formatDate = (d?: string | null) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");

function Tile({ icon: Icon, label, value, hint, to, alert }: { icon: typeof Truck; label: string; value: string; hint?: string; to: string; alert?: boolean }) {
  return (
    <Link to={to} className={`rounded-xl border bg-card p-4 hover:border-primary/50 transition-colors ${alert ? "border-red-500/40" : "border-border"}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`size-4 ${alert ? "text-red-500" : "text-primary"}`} />
      </div>
      <p className="text-2xl font-bold tracking-tight mt-1">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </Link>
  );
}

/** The sample room's day: what to make and send, what is out with buyers, what is pending dispatch, open complaints */
export function MerchandiserDashboard({ data }: { data: MerchandiserSummary }) {
  const totalSamples = data.samples_to_send.length + data.samples_with_buyers.length;
  const onTimeLines = Math.max(0, data.pending_lines - data.overdue_lines);

  const sampleDistribution = [
    {
      label: "To Make & Send",
      value: data.samples_to_send.length,
      sublabel: data.overdue_samples > 0 ? `${data.overdue_samples} overdue` : "Ready to process",
      color: "#3b82f6",
    },
    {
      label: "With Buyers",
      value: data.samples_with_buyers.length,
      sublabel: "Awaiting decision",
      color: "#8b5cf6",
    },
  ].filter((s) => s.value > 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={FlaskConical} label="Samples to send" value={String(data.samples_to_send.length)} hint={data.overdue_samples ? `${data.overdue_samples} past the needed-by date` : "None overdue"} to="/samples" alert={data.overdue_samples > 0} />
        <Tile icon={Truck} label="With buyers" value={String(data.samples_with_buyers.length)} hint="Waiting for a decision" to="/samples" />
        <Tile icon={PackageCheck} label="Pending dispatch" value={String(data.pending_lines)} hint={data.overdue_lines ? `${data.overdue_lines} lines past delivery date` : `${data.orders_open} orders open`} to="/orders" alert={data.overdue_lines > 0} />
        <Tile icon={MessageSquareWarning} label="Open complaints" value={String(data.open_complaints)} hint={data.open_complaints ? `${formatINR(data.open_claims)} claimed${data.oldest_complaint ? ` · oldest ${formatDate(data.oldest_complaint)}` : ""}` : "All clear"} to="/complaints" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard
          title="Sample pipeline status"
          description="Distribution of active sample requests"
          action={<Link to="/samples" className="text-xs text-primary hover:underline">All samples</Link>}
        >
          <DonutPieChart
            data={sampleDistribution}
            donut={true}
            centerValue={String(totalSamples)}
            centerLabel="Active Samples"
            formatValue={(v) => `${v} samples`}
          />
        </SectionCard>

        <SectionCard
          title="Dispatch on-time health"
          description="Pending order lines within delivery schedule"
          action={<Link to="/orders" className="text-xs text-primary hover:underline">View orders</Link>}
        >
          <RadialTargetGauge
            achieved={onTimeLines}
            target={Math.max(1, data.pending_lines)}
            label="On-Time Rate"
            formatMoney={(v) => `${v} lines`}
            size={180}
          />
        </SectionCard>

        <SectionCard
          title="Quality & claims status"
          description="Open claims and customer issues"
          action={<Link to="/complaints" className="text-xs text-primary hover:underline">Review</Link>}
        >
          <div className="flex flex-col justify-between h-full p-2 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-2.5">
                  <ShieldAlert className="size-5 text-amber-600" />
                  <div>
                    <span className="block text-xs font-semibold text-slate-700">Open Claims</span>
                    <span className="block text-[11px] text-muted-foreground">{data.open_complaints} registered issues</span>
                  </div>
                </div>
                <span className="text-base font-bold tabular-nums text-slate-900">{formatINR(data.open_claims)}</span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div>
                  <span className="block text-xs font-semibold text-slate-700">Oldest Issue</span>
                  <span className="block text-[11px] text-muted-foreground">Requires immediate review</span>
                </div>
                <span className="text-xs font-semibold text-slate-900">
                  {data.oldest_complaint ? formatDate(data.oldest_complaint) : "None pending"}
                </span>
              </div>
            </div>

            <Link
              to="/complaints"
              className="w-full text-center py-2 px-3 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-accent transition-colors"
            >
              Manage Quality Complaints &rarr;
            </Link>
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <section className="rounded-xl border border-border bg-card">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-bold">Samples to make and send</h3>
            <Link to="/samples" className="text-xs text-primary hover:underline">All samples</Link>
          </div>
          {data.samples_to_send.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">Nothing waiting. New requests show up here.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.samples_to_send.map((s) => (
                <li key={s.id} className="px-4 py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/deals/${s.deal_id}`} className="text-sm font-medium hover:underline">
                      {s.product_name}
                    </Link>
                    <p className="text-[11px] text-muted-foreground">
                      {s.account_name} · <code className="font-mono">{s.sample_number}</code>
                      {s.sample_status === "REVISION" ? " · revision asked" : ""}
                    </p>
                  </div>
                  <span className={`text-xs whitespace-nowrap flex items-center gap-1 ${s.overdue ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>
                    {s.overdue && <AlertTriangle className="size-3" />}
                    {s.needed_by ? `by ${formatDate(s.needed_by)}` : "no date"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-bold">Out with buyers</h3>
            <Link to="/samples" className="text-xs text-primary hover:underline">Follow up</Link>
          </div>
          {data.samples_with_buyers.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">No samples are waiting for a buyer's decision.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.samples_with_buyers.map((s) => (
                <li key={s.id} className="px-4 py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/deals/${s.deal_id}`} className="text-sm font-medium hover:underline">
                      {s.product_name}
                    </Link>
                    <p className="text-[11px] text-muted-foreground">
                      {s.account_name} · {s.courier}
                      {s.awb_number ? ` · AWB ${s.awb_number}` : ""}
                    </p>
                  </div>
                  <span className={`text-xs whitespace-nowrap ${(s.days_out || 0) >= 7 ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>
                    {s.days_out === 0 ? "today" : `${s.days_out} days ago`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
